import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';

import PageContainer from '../../components/layout/page-container/PageContainer';
import { Paths } from '../../shared/paths';
import { useStyles } from './SuperAdminDashboard.styles';
import theme from '../../styles/theme';
import AppTabs from '../../components/designe/app-tabs/AppTabs';
import Permissions from './permissions/Permissions';
import SubscriptionPlans from './subscription-plans/SubscriptionPlans';
import SubscriptionPlanPricePrices from './subscription-plan-prices/SubscriptionPlanPrices';
import SubscriptionRestrictions from './subscription-restrictions/SubscriptionRestrictions';
import { useAccess } from '../../hooks/useAccess';
import AdminUsers from './admin-users/AdminUsers';
import Other from './other/Other';

const SuperAdminDashboard = (props) => {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const { loading, access } = useAccess();

  if (loading) {
    //handle it
    return null;
  }

  const hasAccess = access?.adminPanels?.canViewSuperAdminPanel;

  if (!loading && !hasAccess) return <Navigate to={Paths.root} />;

  const viewAccess = {
    acl: access?.acl?.canView ?? false,
    subscriptionPlans: access?.subscriptionPlans?.canView ?? false,
    subscriptionPlanPrices: access?.subscriptionPlanPrices?.canView ?? false,
    subscriptionRestrictions:
      access?.subscriptionRestrictions?.canView ?? false,
    adminUsers: access?.adminUsers?.canView ?? false,
    other:
      access?.superAdmin?.canVerifyFirebaseUserEmail === true ||
      access?.superAdmin?.canMigratePublicStoragePaths === true ||
      access?.superAdmin?.canMigrateOwnerStoragePaths === true,
  };

  function getTabsConfig() {
    const tabsConfig = [];

    if (viewAccess.acl) {
      tabsConfig.push({
        label: t('superAdminDashboard.permissions'),
        path: `${Paths.superAdminDashboard.root}${Paths.superAdminDashboard.permissions}`,
      });
    }

    if (viewAccess.adminUsers) {
      tabsConfig.push({
        label: t('superAdminDashboard.adminUsers'),
        path: `${Paths.superAdminDashboard.root}${Paths.superAdminDashboard.adminUsers}`,
      });
    }

    if (viewAccess.subscriptionPlans) {
      tabsConfig.push({
        label: t('superAdminDashboard.subscriptionPlans'),
        path: `${Paths.superAdminDashboard.root}${Paths.superAdminDashboard.subscriptionPlans}`,
      });
    }

    if (viewAccess.subscriptionPlanPrices) {
      tabsConfig.push({
        label: t('superAdminDashboard.subscriptionPlanPrices'),
        path: `${Paths.superAdminDashboard.root}${Paths.superAdminDashboard.subscriptionPlanPrices}`,
      });
    }

    if (viewAccess.subscriptionRestrictions) {
      tabsConfig.push({
        label: t('superAdminDashboard.subscriptionRestrictions'),
        path: `${Paths.superAdminDashboard.root}${Paths.superAdminDashboard.subscriptionRestrictions}`,
      });
    }

    if (viewAccess.other) {
      tabsConfig.push({
        label: t('superAdminDashboard.other'),
        path: `${Paths.superAdminDashboard.root}${Paths.superAdminDashboard.other}`,
      });
    }

    return tabsConfig;
  }

  const tabsConfig = getTabsConfig();
  const firstTabPath = tabsConfig[0]?.path;

  return (
    <PageContainer title={t('superAdminDashboard.superAdminDashboard')}>
      <AppTabs config={tabsConfig} />
      <div className={classes.tabsContent}>
        <Routes>
          {viewAccess.acl && (
            <Route
              path={Paths.superAdminDashboard.permissions}
              element={<Permissions />}
            />
          )}
          {viewAccess.subscriptionPlans && (
            <Route
              path={Paths.superAdminDashboard.subscriptionPlans}
              element={<SubscriptionPlans />}
            />
          )}
          {viewAccess.subscriptionPlanPrices && (
            <Route
              path={Paths.superAdminDashboard.subscriptionPlanPrices}
              element={<SubscriptionPlanPricePrices />}
            />
          )}
          {viewAccess.subscriptionRestrictions && (
            <Route
              path={Paths.superAdminDashboard.subscriptionRestrictions}
              element={<SubscriptionRestrictions />}
            />
          )}
          {viewAccess.adminUsers && (
            <Route
              path={Paths.superAdminDashboard.adminUsers}
              element={<AdminUsers />}
            />
          )}
          {viewAccess.other && (
            <Route path={Paths.superAdminDashboard.other} element={<Other />} />
          )}
          <Route
            path={'/'}
            element={
              firstTabPath ? (
                <Navigate to={firstTabPath} replace />
              ) : (
                <Navigate to={Paths.root} replace />
              )
            }
          />
        </Routes>
      </div>
    </PageContainer>
  );
};

export default SuperAdminDashboard;
